import { listWorkflowEntries, updateWorkflow, type CoworkPeriodWorkflow, type CoworkPortfolioWorkflow } from "@/cowork/api"
import { useCoworkWorkspace } from "@/cowork/workspace-context"
import { useCollectionScope } from "@/cowork/scope-url"
import { useLanguage } from "@/context/language"
import { BackLabel } from "@/components/back-label"
import { Icon as IconV2 } from "@opencode-ai/ui/v2/icon"
import { IconButtonV2 } from "@opencode-ai/ui/v2/icon-button-v2"
import { useParams, useSearchParams } from "@solidjs/router"
import { useAppNavigate } from "@/cowork/workspace-route"
import { createEffect, createMemo, createResource, For, on, Show } from "solid-js"
import { createStore } from "solid-js/store"
import { CoworkWorkflowWizard, type WorkflowWizardScope } from "./cowork-workflow-wizard"
import { CoworkReportingWorkflowDetail } from "./cowork-workflow-runs"
import { readReportingConfig } from "./cowork-workflow-reporting"
import { CoworkWorkflowPipeline, workflowPipelineTab, WorkflowPipelineTabs } from "./cowork-workflow-pipeline"

export function CoworkWorkflowsPage() {
  const language = useLanguage()
  const context = useCoworkWorkspace()
  const scope = useCollectionScope()
  const navigate = useAppNavigate()
  const [state, setState] = createStore({
    search: "",
    saving: "",
    error: false,
    wizard: undefined as WorkflowWizardScope | undefined,
    editingWorkflow: undefined as CoworkPortfolioWorkflow | undefined,
  })
  const [workflows, workflowActions] = createResource(
    () => `${scope().companyID ?? ""}:${scope().projectID ?? ""}`,
    () =>
      listWorkflowEntries({
        entityID: scope().companyID,
        projectID: scope().projectID,
      }),
  )
  const filtered = createMemo(() => {
    const query = state.search.trim().toLowerCase()
    return (workflows.error ? [] : (workflows() ?? [])).filter((workflow) =>
      `${workflow.name} ${workflow.kind === "period" ? "" : workflow.purpose} ${workflow.company_name} ${workflow.project_name} ${workflow.agent_name ?? ""}`
        .toLowerCase()
        .includes(query),
    )
  })
  const editWorkflow = (workflow: CoworkPortfolioWorkflow) => {
    const reporting = readReportingConfig(workflow.workflow_metadata.reporting)
    setState({
      editingWorkflow: workflow,
      wizard: {
        companyID: workflow.company_id,
        projectID: workflow.project_id,
        periodID: reporting
          ? reporting.period_id
          : scope().projectID === workflow.project_id &&
              scope().companyID === workflow.company_id
            ? scope().periodID
            : undefined,
        label: `${workflow.company_name} / ${workflow.project_name}`,
      },
    })
  }
  const toggleWorkflow = async (workflow: CoworkPortfolioWorkflow) => {
    if (state.saving) return
    setState({ saving: workflow.id, error: false })
    try {
      await updateWorkflow(
        workflow.project_id,
        workflow.id,
        { status: workflow.status === "active" ? "disabled" : "active" },
        workflow.company_id,
      )
      await workflowActions.refetch()
    } catch {
      setState("error", true)
    } finally {
      setState("saving", "")
    }
  }
  return (
    <div class="min-h-full bg-v2-background-bg-deep px-4 py-7 sm:px-7 xl:px-10">
      <div class="mx-auto max-w-[1400px]">
        <header class="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 class="text-[30px] font-semibold tracking-[-0.025em]">{language.t("cowork.usage.workflows")}</h1>
            <p class="mt-1 text-[13px] text-v2-text-text-muted">
              {language.t("cowork.enterprise.workflows.description")}
            </p>
          </div>
          <div class="flex flex-wrap gap-2">
            <input
              class="h-10 min-w-[240px] rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 px-3 text-[12px]"
              value={state.search}
              placeholder={language.t("cowork.enterprise.workflows.search")}
              aria-label={language.t("cowork.enterprise.workflows.search")}
              onInput={(event) => setState("search", event.currentTarget.value)}
            />
            <button
              type="button"
              disabled={!scope().projectID || !context.selectedProject() || !!state.wizard || !!state.saving}
              class="h-10 rounded-lg bg-v2-background-bg-contrast px-4 text-[12px] font-semibold text-v2-text-text-contrast disabled:opacity-50"
              onClick={() => {
                const project = context.selectedProject()
                if (!project || project.id !== scope().projectID) return
                setState({
                  editingWorkflow: undefined,
                  wizard: {
                    companyID: project.company_id,
                    projectID: project.id,
                    periodID:
                      scope().periodID,
                    label: [context.selectedEntity()?.name, project.name, context.selectedPeriod()?.name]
                      .filter(Boolean)
                      .join(" / "),
                  },
                })
              }}
            >
              {language.t("cowork.enterprise.workflows.create")}
            </button>
          </div>
        </header>
        <Show when={!scope().projectID}>
          <p class="mt-3 text-[11px] text-v2-text-text-muted">
            {language.t("cowork.enterprise.workflows.selectProjectToCreate")}
          </p>
        </Show>
        <Show when={state.wizard} keyed>
          {(scope) => (
            <CoworkWorkflowWizard
              scope={scope}
              workflow={state.editingWorkflow}
              onCancel={() => {
                setState("wizard", undefined)
                void workflowActions.refetch()
              }}
              onSaved={(workflow) => {
                setState("wizard", undefined)
                navigate(`/workflows/${workflow.id}?tab=runs`)
              }}
              onStarted={(workflowID, runID) => {
                setState("wizard", undefined)
                navigate(`/workflows/${workflowID}?tab=runs&run=${encodeURIComponent(runID)}`)
              }}
            />
          )}
        </Show>
        <Show when={state.error || workflows.error}>
          <div role="alert" class="mt-4 rounded-lg bg-v2-state-bg-danger p-4 text-[12px] text-v2-state-fg-danger">
            {language.t(
              state.error
                ? "cowork.enterprise.workflows.reporting.saveFailed"
                : "cowork.enterprise.workflows.reporting.loadFailed",
            )}
          </div>
        </Show>
        <section class="mt-7 overflow-hidden rounded-xl border border-v2-border-border-base bg-v2-background-bg-base">
          <Show
            when={!workflows.loading && filtered().length > 0}
            fallback={<Empty loading={workflows.loading} text={language.t("cowork.enterprise.workflows.empty")} />}
          >
            <div class="overflow-x-auto">
              <table class="w-full min-w-[700px] border-collapse text-left">
                <thead>
                  <tr class="border-b border-v2-border-border-base text-[10px] font-semibold uppercase tracking-[0.12em] text-v2-text-text-faint">
                    <th class="px-5 py-3">{language.t("cowork.enterprise.workflows.workflow")}</th>
                    <th class="px-4 py-3">{language.t("cowork.usage.status")}</th>
                    <th class="px-4 py-3 text-right">{language.t("cowork.enterprise.workflows.runs")}</th>
                    <th class="px-4 py-3">{language.t("cowork.enterprise.workflows.lastResult")}</th>
                    <th class="px-5 py-3" />
                  </tr>
                </thead>
                <tbody>
                  <For each={filtered()}>
                    {(workflow) => (
                      <tr class="cursor-pointer border-b border-v2-border-border-muted last:border-0 hover:bg-v2-overlay-simple-overlay-hover"
                        onClick={(event) => {
                          if ((event.target as HTMLElement).closest("button, a, input, select")) return
                          navigate(`/workflows/${workflow.id}`)
                        }}>
                        <td class="px-5 py-4">
                          <button type="button" class="text-left" onClick={() => navigate(`/workflows/${workflow.id}`)}>
                            <span class="block text-[13px] font-medium">{workflow.name}</span>
                            <span class="mt-0.5 block max-w-sm truncate text-[10px] text-v2-text-text-muted">
                              {workflow.kind === "period" ? language.t("cowork.enterprise.workflows.data.period") : workflow.purpose || workflow.project_name}
                            </span>
                          </button>
                        </td>
                        <td class="px-4 py-4">
                          <WorkflowStatus status={workflow.status} />
                        </td>
                        <td class="px-4 py-4 text-right text-[12px] tabular-nums">{workflow.runs}</td>
                        <td class="px-4 py-4">
                          {workflow.kind === "period"
                            ? <span class="text-[12px] text-v2-text-text-muted">{language.t("cowork.enterprise.workflows.data.saved")}</span>
                            : <RunStatus status={workflow.last_run_status || "never"} />}
                        </td>
                        <td class="px-5 py-4">
                          <div class="flex justify-end gap-2">
                            <IconButtonV2
                              size="small"
                              variant="ghost"
                              icon={<IconV2 name="outline-square-arrow" size="small" />}
                              aria-label={language.t(workflow.kind === "period" ? "cowork.enterprise.workflows.data.open" : "cowork.enterprise.workflows.reporting.viewRuns")}
                              title={language.t(workflow.kind === "period" ? "cowork.enterprise.workflows.data.open" : "cowork.enterprise.workflows.reporting.viewRuns")}
                              onClick={() => navigate(workflow.kind === "period" ? `/workflows/${workflow.id}` : `/workflows/${workflow.id}?tab=runs`)}
                            />
                            <Show when={workflow.kind !== "period"}>
                            <button
                              type="button"
                              class="rounded-md border border-v2-border-border-base px-3 py-1.5 text-[11px]"
                              disabled={!!state.wizard || !!state.saving}
                              onClick={() => { if (workflow.kind !== "period") editWorkflow(workflow) }}
                            >
                              {language.t("common.edit")}
                            </button>
                            <button
                              type="button"
                              class="rounded-md border border-v2-border-border-base px-3 py-1.5 text-[11px]"
                              disabled={!!state.saving}
                              onClick={() => { if (workflow.kind !== "period") void toggleWorkflow(workflow) }}
                            >
                              {language.t(
                                workflow.status === "active"
                                  ? "cowork.enterprise.workflows.disable"
                                  : "cowork.enterprise.workflows.enable",
                              )}
                            </button>
                            </Show>
                          </div>
                        </td>
                      </tr>
                    )}
                  </For>
                </tbody>
              </table>
            </div>
          </Show>
        </section>
      </div>
    </div>
  )
}

export function CoworkWorkflowDetailPage() {
  const language = useLanguage()
  const context = useCoworkWorkspace()
  const params = useParams<{ workflowId: string }>()
  const navigate = useAppNavigate()
  const [workflows, workflowActions] = createResource(() => listWorkflowEntries())
  const workflow = createMemo(() =>
    workflows.error ? undefined : workflows()?.find((item) => item.id === params.workflowId),
  )
  createEffect(on(() => workflow()?.id, () => {
    const item = workflow()
    if (!item) return
    context.selectEntity(item.company_id)
    context.selectProject(item.project_id)
    const periodID = item.kind === "period" ? item.id : readReportingConfig(item.workflow_metadata.reporting)?.period_id
    context.selectPeriod(periodID || context.allPeriodsID)
  }))
  return (
    <div class="h-full min-h-0 overflow-hidden bg-v2-background-bg-deep">
      <Show
        when={workflow()}
        keyed
        fallback={
          <div class="p-7">
            <Empty
              loading={workflows.loading}
              text={language.t(
                workflows.error
                  ? "cowork.enterprise.workflows.reporting.loadFailed"
                  : "cowork.enterprise.workflows.reporting.notFound",
              )}
            />
            <button type="button" class="mr-4 text-[12px] underline" onClick={() => navigate("/workflows")}>
              <BackLabel>{language.t("cowork.enterprise.workflows.back")}</BackLabel>
            </button>
            <Show when={!workflows.loading}>
              <button type="button" class="text-[12px] underline" onClick={() => void workflowActions.refetch()}>
                {language.t("cowork.enterprise.workflows.reporting.retry")}
              </button>
            </Show>
          </div>
        }
      >
        {(item) => item.kind === "period" ? <CoworkPeriodWorkflowDetail workflow={item} /> : <CoworkReportingWorkflowDetail workflow={item} />}
      </Show>
    </div>
  )
}

export function CoworkPeriodWorkflowDetail(props: { workflow: CoworkPeriodWorkflow }) {
  const language = useLanguage()
  const navigate = useAppNavigate()
  const [query, setQuery] = useSearchParams()
  const tab = () => workflowPipelineTab(query.tab)
  return (
    <div class="flex h-full min-h-0 flex-col overflow-hidden px-4 py-4 sm:px-6">
      <button type="button" class="mb-3 shrink-0 self-start text-[12px] text-v2-text-text-muted" onClick={() => navigate("/workflows")}>
        <BackLabel>{language.t("cowork.enterprise.workflows.back")}</BackLabel>
      </button>
      <header class="mb-3 shrink-0">
        <h1 class="text-[30px] font-semibold">{props.workflow.name}</h1>
        <p class="mt-2 text-[12px] text-v2-text-text-muted">{props.workflow.company_name} / {props.workflow.project_name}</p>
      </header>
      <WorkflowPipelineTabs tab={tab()} onSelect={(value) => setQuery({ tab: value }, { replace: false })} />
      <div class="mt-3 min-h-0 flex-1">
        <CoworkWorkflowPipeline workflow={props.workflow} periodWorkflow periodID={props.workflow.id} tab={tab()} />
      </div>
    </div>
  )
}

function WorkflowStatus(props: { status: string }) {
  const language = useLanguage()
  return (
    <span
      class="rounded-md px-2 py-1 text-[10px] font-medium"
      classList={{
        "bg-v2-state-bg-success text-v2-state-fg-success": props.status === "active",
        "bg-v2-background-bg-layer-03 text-v2-text-text-muted": props.status !== "active",
      }}
    >
      {language.t(props.status === "active" ? "cowork.enterprise.status.active" : "cowork.agents.disabled")}
    </span>
  )
}

function RunStatus(props: { status: string }) {
  const language = useLanguage()
  const keys: Record<string, keyof typeof import("@/i18n/en").dict> = {
    never: "cowork.enterprise.workflows.never",
    created: "cowork.enterprise.workflows.status.created",
    running: "cowork.enterprise.workflows.status.running",
    completed: "cowork.enterprise.workflows.status.completed",
    needs_review: "cowork.enterprise.workflows.status.needsReview",
    failed: "cowork.enterprise.workflows.status.failed",
  }
  return (
    <span class="rounded-md bg-v2-background-bg-layer-03 px-2 py-1 text-[10px] font-medium">
      {language.t(keys[props.status] || "cowork.enterprise.workflows.reporting.stage.unknown")}
    </span>
  )
}

function Empty(props: { loading: boolean; text: string }) {
  return (
    <div class="grid min-h-48 place-items-center p-8 text-center text-[12px] text-v2-text-text-muted">
      {props.loading ? (
        <span class="size-5 animate-spin rounded-full border-2 border-v2-border-border-base border-t-v2-icon-icon-base" />
      ) : (
        props.text
      )}
    </div>
  )
}
